import {Observable} from 'rxjs';
import {CommonErrorResolver} from './common.error.resolver';
import {Request, Response, XHRBackend, XHRConnection, BrowserXhr, ResponseOptions, XSRFStrategy} from '@angular/http';
import {ExceptionFactory} from "./exception/exception.factory";

export class CommonXhrBackend extends XHRBackend {

    constructor(browser: BrowserXhr,
                options: ResponseOptions,
                strategy: XSRFStrategy,
                private _errorResolver: CommonErrorResolver) {
        super(browser, options, strategy);
    }

    /**
     * Back-office screens that show their own errors (inline message, retry, empty state): for these URLs an HTTP
     * error reaches the screen's error callback instead of the global handler. Session and permission errors (401,
     * 403) stay global, and a call without an error callback still goes to the global handler as before.
     */
    private static readonly SCREEN_HANDLED_URLS = ['/api/backoffice/open-banking/', '/api/backoffice/finxp/errors'];

    /**
     * Same, for URLs with ids in the middle: the customer "Users" tab (users, roles, linked accounts and 2FA)
     * and the customer "API access" tab (eapi consumers).
     */
    private static readonly SCREEN_HANDLED_URL_PATTERNS = [
        /api\/backoffice\/customers\/\d+\/(users\/|new-user$|authorised-user$|update\/state$)/,
        /api\/backoffice\/customers\/\d+\/api-access(\/|$)/,
        /api\/backoffice\/customers\/(account|delete-account-role|profile-user-role\/details|twoFa)\//,
        /api\/backoffice\/customers\/profile\/\d+\/role-accounts\//
    ];

    createConnection(request: Request): XHRConnection {
        let connection = super.createConnection(request);
        connection.response = this.wrapObservable(connection.response, request && request.url);
        return connection;
    }

    wrapObservable(response: Observable<Response>, url?: string): Observable<Response> {
        return Observable.create((observer) => {
            response.subscribe(
                (value) => {
                    observer.next(value)
                },

                (error) => {
                    if (this.handledByScreen(url, error)) {
                        try {
                            observer.error(this._errorResolver.toErrorResponse(error));
                        } catch (e) {// no error callback (or it failed): the global handler, exactly as without this
                            let resolved = this._errorResolver.resolve(error);
                            throw ExceptionFactory.createServerException(resolved.status, resolved.message, resolved.code)
                        }
                        return;
                    }
                    let errorResponse = this._errorResolver.resolve(error);
                    try {
                        observer.error(errorResponse);
                    } catch (e) {//fallback to default error handler if either we haven't provided Observable.error() callback or it threw an exception
                        throw ExceptionFactory.createServerException(errorResponse.status, errorResponse.message, errorResponse.code)
                    }
                },

                () => {
                    observer.complete()
                }
            );
        })
    }

    private handledByScreen(url: string, error: any): boolean {
        if (!url || !error || typeof error.status !== 'number' || error.status === 401 || error.status === 403) {
            return false;
        }
        return CommonXhrBackend.SCREEN_HANDLED_URLS.some(prefix => url.indexOf(prefix) >= 0)
            || CommonXhrBackend.SCREEN_HANDLED_URL_PATTERNS.some(pattern => pattern.test(url.split('?')[0]));
    }
}


// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/angular-router-loader/src!./src/common/providers/common.xhr.backend.ts